<div class="firmware-update-container" *ngIf="authService.hasRolesAdminOrDistributor()">
  <!-- Header -->
  <div class="header">
    <div class="header-content">
      <mat-icon class="header-icon">system_update</mat-icon>
      <div class="header-text">
        <h2>{{ 'FirmwareUpdate.Title' | translate }}</h2>
        <p class="subtitle">{{ 'FirmwareUpdate.Subtitle' | translate }}</p>
      </div>
    </div>
    <div class="header-actions">
      <button mat-stroked-button [color]="currentView === 'devices' ? 'primary' : ''" (click)="switchView('devices')">
        <mat-icon>devices</mat-icon>
        {{ 'FirmwareUpdate.DevicesView' | translate }}
      </button>
      <button mat-stroked-button *ngIf="isUpdating || queueDevices.length > 0"
        [color]="currentView === 'progress' ? 'primary' : ''" (click)="switchView('progress')">
        <mat-icon>sync</mat-icon>
        {{ 'FirmwareUpdate.ProgressView' | translate }}
        <span class="pulse-dot" *ngIf="isQueueRunning"></span>
      </button>
      <button mat-stroked-button [color]="currentView === 'history' ? 'primary' : ''" (click)="switchView('history')">
        <mat-icon>history</mat-icon>
        {{ 'FirmwareUpdate.HistoryView' | translate }}
      </button>
      <button mat-icon-button *ngIf="authService.hasRoleAdmin()" (click)="openConfigDialog()"
        matTooltip="{{ 'FirmwareUpdate.ConfigTitle' | translate }}" class="config-button">
        <mat-icon>settings</mat-icon>
      </button>
    </div>
  </div>

  <!-- Available Firmware Info -->
  <div class="firmware-info-bar" *ngIf="availableFirmware?.length > 0 && currentView === 'devices'">
    <mat-icon>info_outline</mat-icon>
    <span class="info-label">{{ 'FirmwareUpdate.AvailableVersions' | translate }}:</span>
    <span class="firmware-version" *ngFor="let fw of uniqueFirmwareVersions">
      {{ fw.firmwareType }} <strong>v{{ fw.version }}</strong>
    </span>
  </div>

  <div class="filter-bar" *ngIf="currentView === 'devices'">
    <div class="filter-row">
      <mat-form-field appearance="outline" class="filter-field company-field">
        <mat-label>{{ 'FirmwareUpdate.FilterByCompany' | translate }}</mat-label>
        <input matInput [(ngModel)]="companySearchText" (ngModelChange)="onCompanySearch($event)"
          [matAutocomplete]="companyAuto" (blur)="onCompanyBlur()">
        <mat-icon matSuffix *ngIf="companySearchText" (click)="clearCompany()" style="cursor:pointer">close</mat-icon>
        <mat-autocomplete #companyAuto="matAutocomplete" (optionSelected)="onCompanySelected($event)"
          [displayWith]="displayCompanyName.bind(this)">
          <mat-option *ngFor="let c of filteredCompanies" [value]="c._id">{{ c.name }}</mat-option>
        </mat-autocomplete>
      </mat-form-field>

      <mat-form-field appearance="outline" class="filter-field apikey-field">
        <mat-label>{{ 'FirmwareUpdate.FilterByReceptora' | translate }}</mat-label>
        <mat-select [(ngModel)]="selectedApiKey" (selectionChange)="onApiKeyChange()" [disabled]="!selectedCompany">
          <mat-option value="">—</mat-option>
          <mat-option *ngFor="let r of receiverOptions" [value]="r.apiKey">{{ r.name }}</mat-option>
        </mat-select>
      </mat-form-field>
    </div>

    <div class="filter-row">
      <mat-form-field appearance="outline" class="filter-field type-field">
        <mat-label>{{ 'FirmwareUpdate.FilterByType' | translate }}</mat-label>
        <mat-select [(ngModel)]="selectedFirmwareType" (selectionChange)="onStatusFilterChange()"
          [disabled]="!selectedCompany">
          <mat-option value="">—</mat-option>
          <mat-option *ngFor="let t of firmwareTypes" [value]="t">{{ t }}</mat-option>
        </mat-select>
      </mat-form-field>

      <mat-form-field appearance="outline" class="filter-field mac-field">
        <mat-label>{{ 'FirmwareUpdate.SearchMac' | translate }}</mat-label>
        <input matInput [(ngModel)]="macSearch" (ngModelChange)="onMacSearch()" [disabled]="!selectedCompany">
        <mat-icon matSuffix>search</mat-icon>
      </mat-form-field>
    </div>

    <div class="status-filters" [class.disabled]="!selectedCompany">
      <span class="status-filters-label">{{ 'FirmwareUpdate.ShowStatus' | translate }}:</span>
      <mat-checkbox [(ngModel)]="showUpToDate" (change)="onStatusFilterChange()" [disabled]="!selectedCompany"
        class="status-checkbox status-check-uptodate">
        {{ 'FirmwareUpdate.StatusUpToDate' | translate }}
      </mat-checkbox>
      <mat-checkbox [(ngModel)]="showOutdated" (change)="onStatusFilterChange()" [disabled]="!selectedCompany"
        class="status-checkbox status-check-outdated">
        {{ 'FirmwareUpdate.StatusOutdated' | translate }}
      </mat-checkbox>
      <mat-checkbox [(ngModel)]="showUnknown" (change)="onStatusFilterChange()" [disabled]="!selectedCompany"
        class="status-checkbox status-check-unknown">
        {{ 'FirmwareUpdate.StatusUnknown' | translate }}
      </mat-checkbox>
    </div>
  </div>

  <!-- Device Table -->
  <div class="devices-section" *ngIf="currentView === 'devices'">
    <!-- No company selected -->
    <div class="empty-state" *ngIf="!selectedCompany">
      <mat-icon>business</mat-icon>
      <p>{{ 'FirmwareUpdate.SelectCompanyFirst' | translate }}</p>
    </div>

    <!-- Loading -->
    <div class="loading-state" *ngIf="selectedCompany && isLoading">
      <mat-spinner diameter="40"></mat-spinner>
    </div>

    <!-- No devices found -->
    <div class="empty-state" *ngIf="selectedCompany && !isLoading && devices?.length === 0">
      <mat-icon>devices_other</mat-icon>
      <p>{{ 'FirmwareUpdate.NoDevicesFound' | translate }}</p>
    </div>

    <!-- Devices table -->
    <mat-card class="devices-card" *ngIf="selectedCompany && !isLoading && devices?.length > 0">
      <mat-card-content>
        <!-- Selection info bar -->
        <div class="selection-bar" *ngIf="getSelectedCount() > 0">
          <span class="selection-count">{{ getSelectedCount() }} {{ 'FirmwareUpdate.DevicesSelected' | translate
            }}</span>
          <button mat-raised-button color="primary" (click)="startUpdate()" class="update-button"
            [disabled]="isLoading">
            <mat-icon>cloud_upload</mat-icon>
            {{ 'FirmwareUpdate.UpdateSelected' | translate }} ({{ getSelectedCount() }})
          </button>
        </div>

        <div class="table-container">
          <table class="device-table">
            <thead>
              <tr>
                <th class="col-checkbox">
                  <mat-checkbox [(ngModel)]="selectAll" (change)="toggleSelectAll()"></mat-checkbox>
                </th>
                <th class="col-mac">{{ 'FirmwareUpdate.ColumnMac' | translate }}</th>
                <th class="col-apikey">{{ 'FirmwareUpdate.ColumnApiKey' | translate }}</th>
                <th class="col-type">{{ 'FirmwareUpdate.ColumnType' | translate }}</th>
                <th class="col-version">{{ 'FirmwareUpdate.ColumnVersion' | translate }}</th>
                <th class="col-status">{{ 'FirmwareUpdate.ColumnStatus' | translate }}</th>
                <th class="col-modality">{{ 'FirmwareUpdate.ColumnModality' | translate }}</th>
                <th class="col-target-version">{{ 'FirmwareUpdate.ColumnTargetVersion' | translate }}</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let device of filteredDevices" [class.selected-row]="device.selected"
                [class.blocked-row]="isRowBlocked(device)">
                <td class="col-checkbox">
                  <mat-checkbox [(ngModel)]="device.selected" (change)="toggleDevice(device)"
                    [disabled]="isRowBlocked(device)"></mat-checkbox>
                </td>
                <td class="col-mac">
                  <span class="mac-cell">{{ formatMac(device.mac) }}</span>
                </td>
                <td class="col-apikey">{{ getReceiverName(device.apiKey) }}</td>
                <td class="col-type">{{ device.firmwareType }}</td>
                <td class="col-version">
                  <span *ngIf="device.firmVersion">v{{ device.firmVersion }}</span>
                  <span *ngIf="!device.firmVersion" class="no-data">—</span>
                </td>
                <td class="col-status">
                  <span class="status-badge" [ngClass]="'status-' + device.updateStatus">
                    {{ ('FirmwareUpdate.Status' + (device.updateStatus === 'up_to_date' ? 'UpToDate' :
                    device.updateStatus === 'outdated' ? 'Outdated' : 'Unknown')) | translate }}
                  </span>
                </td>
                <td class="col-modality">
                  <span *ngIf="isRowBlocked(device)" class="blocked-reason" [matTooltip]="device.deviceOptions?.blockedReason">
                    <mat-icon color="warn">block</mat-icon>
                    {{ 'FirmwareUpdate.Blocked' | translate }}
                  </span>
                  <div *ngIf="!isRowBlocked(device)" class="modality-cell-content">
                    <mat-form-field appearance="outline" class="modality-field">
                      <mat-select [(ngModel)]="device.selectedModalityKey" (selectionChange)="onModalityChange(device)"
                        [disabled]="!isRowReady(device)">
                        <mat-option *ngFor="let m of getModalities(device)" [value]="m.key">{{ m.label }}</mat-option>
                      </mat-select>
                    </mat-form-field>
                    <mat-icon *ngIf="hasModalityWarning(device)" color="warn" class="modality-warning-icon"
                      [matTooltip]="'FirmwareUpdate.ZigbeeModuleWarning' | translate">warning</mat-icon>
                  </div>
                </td>
                <td class="col-target-version">
                  <ng-container *ngIf="!isRowBlocked(device)">
                    <mat-form-field *ngIf="hasMultipleVersions(device); else singleVersion" appearance="outline"
                      class="version-field">
                      <mat-select [(ngModel)]="device.selectedVersion" [disabled]="!isRowReady(device)">
                        <mat-option *ngFor="let v of getSelectedModalityVersions(device)" [value]="v.version">
                          v{{ v.version }}
                        </mat-option>
                      </mat-select>
                    </mat-form-field>
                    <ng-template #singleVersion>
                      <span class="single-version-label">v{{ device.selectedVersion || '—' }}</span>
                    </ng-template>
                  </ng-container>
                  <span *ngIf="isRowBlocked(device)" class="no-data">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <mat-paginator [length]="totalDevices" [pageSize]="pageSize" [pageIndex]="currentPage"
          [pageSizeOptions]="[10, 25, 50, 100]" (page)="onPageChange($event)" showFirstLastButtons></mat-paginator>
      </mat-card-content>
    </mat-card>
  </div>

  <!-- Progress Section (Global Queue) -->
  <div class="progress-section" *ngIf="currentView === 'progress' && queueDevices.length > 0">
    <mat-card class="progress-card">
      <mat-card-content>
        <!-- Queue Header -->
        <div class="job-header">
          <div class="job-info">
            <h3>
              {{ 'FirmwareUpdate.JobProgress' | translate }}
              <span class="job-status-badge" [ngClass]="'job-' + queueStatus">
                {{ 'FirmwareUpdate.JobStatus.' + queueStatus | translate }}
              </span>
            </h3>
          </div>
          <div class="job-actions">
            <button mat-stroked-button color="warn" (click)="cancelUpdate()" *ngIf="isQueueRunning"
              [disabled]="!hasQueueSelection()" class="cancel-button">
              <mat-icon>cancel</mat-icon>
              {{ 'FirmwareUpdate.Cancel' | translate }} ({{ selectedQueueMacs.size }})
            </button>
            <button mat-stroked-button color="warn" (click)="retryFailedDevices()"
              *ngIf="isJobFinished() && hasFailedDevices()" class="retry-failed-button"
              [disabled]="isLoading">
              <mat-icon>replay</mat-icon>
              {{ 'FirmwareUpdate.RetryFailed' | translate }} ({{ getFailedCount() }})
            </button>
            <button mat-stroked-button (click)="resetView()" *ngIf="isJobFinished()" class="new-update-button">
              <mat-icon>refresh</mat-icon>
              {{ 'FirmwareUpdate.NewUpdate' | translate }}
            </button>
          </div>
        </div>

        <!-- Auto-refresh cortado por inactividad: banner persistente, no un snackbar que desaparece solo -->
        <div class="polling-inactivity-banner" *ngIf="pollingStoppedInactivity">
          <mat-icon color="warn">warning</mat-icon>
          <span>{{ 'FirmwareUpdate.PollingStoppedInactivity' | translate }}</span>
          <button mat-stroked-button color="warn" (click)="resumePollingIfInactive()">
            <mat-icon>refresh</mat-icon>
            {{ 'FirmwareUpdate.Refresh' | translate }}
          </button>
        </div>

        <!-- Summary counters -->
        <div class="summary-counters" *ngIf="queueDevices.length > 0">
          <div class="counter active">
            <mat-icon>sync</mat-icon>
            <span class="counter-value">{{ getActiveCount() }}</span>
            <span class="counter-label">{{ 'FirmwareUpdate.Active' | translate }}</span>
          </div>
          <div class="counter waiting">
            <mat-icon>schedule</mat-icon>
            <span class="counter-value">{{ getWaitingCount() }}</span>
            <span class="counter-label">{{ 'FirmwareUpdate.Waiting' | translate }}</span>
          </div>
          <div class="counter completed">
            <mat-icon>check_circle</mat-icon>
            <span class="counter-value">{{ getCompletedCount() }}</span>
            <span class="counter-label">{{ 'FirmwareUpdate.Completed' | translate }}</span>
          </div>
          <div class="counter failed">
            <mat-icon>error</mat-icon>
            <span class="counter-value">{{ getFailedCount() }}</span>
            <span class="counter-label">{{ 'FirmwareUpdate.Failed' | translate }}</span>
          </div>
          <div class="counter rejected">
            <mat-icon>block</mat-icon>
            <span class="counter-value">{{ getRejectedCount() }}</span>
            <span class="counter-label">{{ 'FirmwareUpdate.Rejected' | translate }}</span>
          </div>
          <div class="counter total">
            <mat-icon>devices</mat-icon>
            <span class="counter-value">{{ getTotalCount() }}</span>
            <span class="counter-label">{{ 'FirmwareUpdate.Total' | translate }}</span>
          </div>
        </div>

        <!-- Devices Table -->
        <table mat-table [dataSource]="queueDevices" class="progress-table">
          <!-- Select Column -->
          <ng-container matColumnDef="select">
            <th mat-header-cell *matHeaderCellDef>
              <mat-checkbox [checked]="queueSelectAll" (change)="toggleAllQueueDevices()"></mat-checkbox>
            </th>
            <td mat-cell *matCellDef="let device">
              <mat-checkbox *ngIf="isCancelable(device)" [checked]="selectedQueueMacs.has(device.mac)"
                (change)="toggleQueueDevice(device)"></mat-checkbox>
            </td>
          </ng-container>

          <!-- MAC Column -->
          <ng-container matColumnDef="mac">
            <th mat-header-cell *matHeaderCellDef>{{ 'FirmwareUpdate.Mac' | translate }}</th>
            <td mat-cell *matCellDef="let device">
              <span class="mac-cell">{{ formatMac(device.mac) }}</span>
            </td>
          </ng-container>

          <!-- Receiver Column -->
          <ng-container matColumnDef="receiver">
            <th mat-header-cell *matHeaderCellDef>{{ 'FirmwareUpdate.Receiver' | translate }}</th>
            <td mat-cell *matCellDef="let device">
              <span class="receiver-cell">{{ getReceiverName(device.apiKey) }}</span>
            </td>
          </ng-container>

          <!-- Status Column -->
          <ng-container matColumnDef="status">
            <th mat-header-cell *matHeaderCellDef>{{ 'FirmwareUpdate.StatusLabel' | translate }}</th>
            <td mat-cell *matCellDef="let device">
              <div class="status-cell" [ngClass]="getStatusClass(device.status)">
                <mat-spinner
                  *ngIf="device.status !== 'completed' && device.status !== 'failed' && device.status !== 'rejected' && device.status !== 'queued' && device.status !== 'waiting'"
                  diameter="16" class="status-spinner"></mat-spinner>
                <mat-icon *ngIf="device.status === 'completed'" class="status-icon">check_circle</mat-icon>
                <mat-icon *ngIf="device.status === 'failed'" class="status-icon">error</mat-icon>
                <mat-icon *ngIf="device.status === 'rejected'" class="status-icon">block</mat-icon>
                <mat-icon *ngIf="device.status === 'queued' || device.status === 'waiting'" class="status-icon">
                  schedule</mat-icon>
                <span>{{ getStatusLabel(device) }}</span>
              </div>
            </td>
          </ng-container>

          <!-- Progress Column -->
          <ng-container matColumnDef="progress">
            <th mat-header-cell *matHeaderCellDef>{{ 'FirmwareUpdate.Progress' | translate }}</th>
            <td mat-cell *matCellDef="let device">
              <div class="progress-cell" *ngIf="device.status === 'sending_data' || device.status === 'completed'">
                <mat-progress-bar mode="determinate" [value]="device.progress" class="progress-bar"></mat-progress-bar>
                <span class="progress-text">{{ device.progress | number: '1.0-0' }}%</span>
              </div>
              <span *ngIf="device.status !== 'sending_data' && device.status !== 'completed'" class="no-data">—</span>
            </td>
          </ng-container>

          <!-- Error Column -->
          <ng-container matColumnDef="error">
            <th mat-header-cell *matHeaderCellDef>{{ 'FirmwareUpdate.Error' | translate }}</th>
            <td mat-cell *matCellDef="let device">
              <span *ngIf="device.error" class="error-text" [matTooltip]="device.error">
                {{ device.error?.length > 50 ? (device.error | slice: 0:50) + '...' : device.error }}
              </span>
              <span *ngIf="device.rejectionReason" class="rejection-text" [matTooltip]="device.rejectionReason">
                {{ device.rejectionReason?.length > 50 ? (device.rejectionReason | slice: 0:50) + '...' :
                device.rejectionReason }}
              </span>
              <span *ngIf="!device.error && !device.rejectionReason" class="no-data">—</span>
            </td>
          </ng-container>

          <!-- Actions Column -->
          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef></th>
            <td mat-cell *matCellDef="let device">
              <button mat-icon-button *ngIf="isRetriable(device)" (click)="retryDevice(device)"
                matTooltip="Reintentar actualización" color="primary" class="retry-button">
                <mat-icon>replay</mat-icon>
              </button>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
        </table>
      </mat-card-content>
    </mat-card>
  </div>

  <!-- Empty queue state -->
  <div class="progress-section" *ngIf="currentView === 'progress' && queueDevices.length === 0">
    <mat-card class="progress-card">
      <mat-card-content>
        <div class="empty-state">
          <mat-icon>sync_disabled</mat-icon>
          <p>{{ 'FirmwareUpdate.NoActiveUpdates' | translate }}</p>
          <button mat-stroked-button (click)="switchView('devices')" class="new-update-button">
            <mat-icon>devices</mat-icon>
            {{ 'FirmwareUpdate.DevicesView' | translate }}
          </button>
        </div>
      </mat-card-content>
    </mat-card>
  </div>

  <!-- History Section -->
  <div class="history-section" *ngIf="currentView === 'history'">
    <mat-card class="progress-card">
      <mat-card-content>
        <div class="job-header">
          <div class="job-info">
            <h3>
              <mat-icon class="header-icon-inline">history</mat-icon>
              {{ 'FirmwareUpdate.HistoryTitle' | translate }}
            </h3>
            <span class="job-details">{{ 'FirmwareUpdate.HistorySubtitle' | translate }}</span>
          </div>
          <div class="job-actions">
            <button mat-stroked-button (click)="loadHistory()" class="new-update-button">
              <mat-icon>refresh</mat-icon>
              {{ 'FirmwareUpdate.Refresh' | translate }}
            </button>
          </div>
        </div>

        <div class="loading-state" *ngIf="isLoadingHistory">
          <mat-spinner diameter="40"></mat-spinner>
        </div>

        <div class="empty-state" *ngIf="!isLoadingHistory && historyDevices.length === 0">
          <mat-icon>info_outline</mat-icon>
          <p>{{ 'FirmwareUpdate.NoHistory' | translate }}</p>
        </div>

        <div class="filter-bar history-filter-bar" *ngIf="!isLoadingHistory && historyDevices.length > 0">
          <mat-form-field appearance="outline" class="filter-field company-field">
            <mat-label>{{ 'FirmwareUpdate.FilterByCompany' | translate }}</mat-label>
            <input matInput [(ngModel)]="historyCompanySearch" (ngModelChange)="onHistoryCompanySearch()">
            <mat-icon matSuffix *ngIf="historyCompanySearch" (click)="clearHistoryCompanySearch()"
              style="cursor:pointer">close</mat-icon>
            <mat-icon matSuffix *ngIf="!historyCompanySearch">search</mat-icon>
          </mat-form-field>
        </div>

        <div class="summary-counters" *ngIf="!isLoadingHistory && historyDevices.length > 0">
          <div class="counter completed">
            <mat-icon>check_circle</mat-icon>
            <span class="counter-value">{{ getHistoryCountByStatus('completed') }}</span>
            <span class="counter-label">{{ 'FirmwareUpdate.Completed' | translate }}</span>
          </div>
          <div class="counter failed">
            <mat-icon>error</mat-icon>
            <span class="counter-value">{{ getHistoryCountByStatus('failed') }}</span>
            <span class="counter-label">{{ 'FirmwareUpdate.Failed' | translate }}</span>
          </div>
          <div class="counter rejected">
            <mat-icon>block</mat-icon>
            <span class="counter-value">{{ getHistoryCountByStatus('rejected') }}</span>
            <span class="counter-label">{{ 'FirmwareUpdate.Rejected' | translate }}</span>
          </div>
          <div class="counter total">
            <mat-icon>devices</mat-icon>
            <span class="counter-value">{{ filteredHistoryDevices.length }}</span>
            <span class="counter-label">{{ 'FirmwareUpdate.Total' | translate }}</span>
          </div>
        </div>

        <table mat-table [dataSource]="filteredHistoryDevices" class="progress-table" matSort
          (matSortChange)="onHistorySortChange($event)" *ngIf="!isLoadingHistory && filteredHistoryDevices.length > 0">

          <ng-container matColumnDef="mac">
            <th mat-header-cell *matHeaderCellDef>{{ 'FirmwareUpdate.Mac' | translate }}</th>
            <td mat-cell *matCellDef="let device">
              <span class="mac-cell">{{ formatMac(device.mac) }}</span>
            </td>
          </ng-container>

          <ng-container matColumnDef="company">
            <th mat-header-cell *matHeaderCellDef mat-sort-header="company">{{ 'FirmwareUpdate.Company' | translate }}
            </th>
            <td mat-cell *matCellDef="let device">
              <span>{{ device.companyName || '—' }}</span>
            </td>
          </ng-container>

          <ng-container matColumnDef="status">
            <th mat-header-cell *matHeaderCellDef>{{ 'FirmwareUpdate.StatusLabel' | translate }}</th>
            <td mat-cell *matCellDef="let device">
              <div class="status-cell" [ngClass]="getStatusClass(device.status)">
                <mat-icon *ngIf="device.status === 'completed'" class="status-icon">check_circle</mat-icon>
                <mat-icon *ngIf="device.status === 'failed'" class="status-icon">error</mat-icon>
                <mat-icon *ngIf="device.status === 'rejected'" class="status-icon">block</mat-icon>
                <mat-icon
                  *ngIf="device.status !== 'completed' && device.status !== 'failed' && device.status !== 'rejected'"
                  class="status-icon">schedule</mat-icon>
                <span>{{ getStatusLabel(device) }}</span>
              </div>
            </td>
          </ng-container>

          <ng-container matColumnDef="firmwareVersion">
            <th mat-header-cell *matHeaderCellDef>{{ 'FirmwareUpdate.FirmwareVersion' | translate }}</th>
            <td mat-cell *matCellDef="let device">
              {{ device.firmwareVersion || '—' }}
            </td>
          </ng-container>

          <ng-container matColumnDef="modality">
            <th mat-header-cell *matHeaderCellDef>{{ 'FirmwareUpdate.ColumnModality' | translate }}</th>
            <td mat-cell *matCellDef="let device">
              <span *ngIf="device.isModalityChange" class="modality-change-badge" [matTooltip]="device.auditMessage">
                <mat-icon>swap_horiz</mat-icon>
                {{ device.previousFirmwareType }} → {{ device.firmwareType }}
              </span>
              <span *ngIf="!device.isModalityChange" class="no-data">—</span>
            </td>
          </ng-container>

          <ng-container matColumnDef="error">
            <th mat-header-cell *matHeaderCellDef>{{ 'FirmwareUpdate.Error' | translate }}</th>
            <td mat-cell *matCellDef="let device">
              <span *ngIf="device.error" class="error-text" [matTooltip]="device.error">
                {{ device.error?.length > 50 ? (device.error | slice: 0:50) + '...' : device.error }}
              </span>
              <span *ngIf="device.rejectionReason" class="rejection-text" [matTooltip]="device.rejectionReason">
                {{ device.rejectionReason?.length > 50 ? (device.rejectionReason | slice: 0:50) + '...' :
                device.rejectionReason }}
              </span>
              <span *ngIf="!device.error && !device.rejectionReason" class="no-data">—</span>
            </td>
          </ng-container>

          <ng-container matColumnDef="updatedAt">
            <th mat-header-cell *matHeaderCellDef>{{ 'FirmwareUpdate.UpdatedAt' | translate }}</th>
            <td mat-cell *matCellDef="let device">
              {{ device.updatedAt | date:'dd/MM/yyyy HH:mm' }}
            </td>
          </ng-container>

          <!-- Actions Column -->
          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef></th>
            <td mat-cell *matCellDef="let device">
              <button mat-icon-button *ngIf="isRetriable(device)" (click)="retryDevice(device)"
                matTooltip="Reintentar actualización" color="primary" class="retry-button">
                <mat-icon>replay</mat-icon>
              </button>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="historyColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: historyColumns"></tr>
        </table>

        <div class="empty-state"
          *ngIf="!isLoadingHistory && historyDevices.length > 0 && filteredHistoryDevices.length === 0">
          <mat-icon>search_off</mat-icon>
          <p>{{ 'FirmwareUpdate.NoHistory' | translate }}</p>
        </div>
      </mat-card-content>
    </mat-card>
  </div>
</div>